import { useState, useEffect } from 'react';
import { api, formatCurrency } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import NumericKeypad from '../components/NumericKeypad';

const EMPTY = { name:'', total_amount:'', remaining_amount:'', interest_rate:'', due_date:'', type:'owe', icon:'💳' };
const ICONS = ['💳','🏦','🏠','🚗','📱','💊','🎓','⚡','💵','🤝'];

export default function Debts() {
  const { user } = useAuth();
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const [activeKeypad, setActiveKeypad] = useState(null); // 'total', 'remaining', 'pay'
  const [payModal, setPayModal] = useState(null);
  const [payAmt, setPayAmt] = useState('');
  const currency = user?.currency || 'COP';

  const load = async () => {
    setLoading(true);
    try { setDebts(await api.get('/debts')); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setShowModal(true); };
  const openEdit = (d) => {
    setForm({ name:d.name, total_amount:d.total_amount, remaining_amount:d.remaining_amount,
      interest_rate:d.interest_rate, due_date:d.due_date||'', type:d.type, icon:d.icon });
    setEditId(d.id); setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    const payload = { ...form, remaining_amount: form.remaining_amount || form.total_amount };
    try {
      if (editId) { await api.put(`/debts/${editId}`, payload); toast.success('Deuda actualizada'); }
      else { await api.post('/debts', payload); toast.success('Deuda registrada'); }
      setShowModal(false); load();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta deuda?')) return;
    try { await api.delete(`/debts/${id}`); toast.success('Eliminada'); load(); }
    catch (err) { toast.error(err.message); }
  };

  const handlePay = async () => {
    if (!payAmt) return;
    const d = debts.find(x => x.id === payModal);
    const newRemaining = Math.max(d.remaining_amount - parseFloat(payAmt), 0);
    try {
      await api.put(`/debts/${payModal}`, { ...d, remaining_amount: newRemaining });
      toast.success(newRemaining === 0 ? '🎉 ¡Deuda pagada!' : 'Pago registrado');
      setPayModal(null); setPayAmt(''); load();
    } catch (err) { toast.error(err.message); }
  };

  const totalOwed = debts.filter(d=>d.type==='owe').reduce((s,d)=>s+d.remaining_amount,0);
  const totalOwedToMe = debts.filter(d=>d.type==='owed').reduce((s,d)=>s+d.remaining_amount,0);

  return (
    <div style={styles.page} className="animate-fadeIn">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem' }}>Deudas</h2>
        <button className="btn btn-primary" onClick={openAdd}>+ Nueva</button>
      </div>

      {/* Summary */}
      {debts.length > 0 && (
        <div style={styles.summaryGrid}>
          <div className="card card-sm" style={{ borderColor:'rgba(255,84,112,0.2)' }}>
            <p style={{ fontSize:'0.75rem', color:'var(--text-secondary)' }}>Debo</p>
            <p style={{ fontWeight:800, fontSize:'1.2rem', color:'var(--red)', marginTop:4, fontFamily:'var(--font-display)' }}>
              {formatCurrency(totalOwed, currency)}
            </p>
          </div>
          <div className="card card-sm" style={{ borderColor:'rgba(0,212,170,0.2)' }}>
            <p style={{ fontSize:'0.75rem', color:'var(--text-secondary)' }}>Me deben</p>
            <p style={{ fontWeight:800, fontSize:'1.2rem', color:'var(--green)', marginTop:4, fontFamily:'var(--font-display)' }}>
              {formatCurrency(totalOwedToMe, currency)}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      {['owe','owed'].map(type => {
        const list = debts.filter(d => d.type === type);
        if (!list.length && debts.length > 0) return null;
        return (
          <div key={type} style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {debts.length > 0 && (
              <h3 style={{ fontSize:'0.9rem', color:'var(--text-secondary)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                {type === 'owe' ? '💳 Deudas que tengo' : '🤝 Me deben'}
              </h3>
            )}
            {loading ? null : list.map(d => {
              const pct = d.total_amount > 0 ? Math.min(((d.total_amount - d.remaining_amount)/d.total_amount)*100,100) : 0;
              const paid = pct >= 100;
              const daysLeft = d.due_date ? Math.ceil((new Date(d.due_date) - new Date())/86400000) : null;
              return (
                <div key={d.id} className="card">
                  <div style={{ display:'flex', gap:12, marginBottom:14, alignItems:'flex-start' }}>
                    <div style={{ width:44, height:44, borderRadius:12, background:'var(--bg-input)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
                      {d.icon}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <p style={{ fontWeight:700 }}>{d.name}</p>
                        {paid && <span className="badge badge-green">Pagada</span>}
                      </div>
                      <div style={{ display:'flex', gap:8, marginTop:4, flexWrap:'wrap' }}>
                        {d.interest_rate > 0 && (
                          <span className="badge badge-yellow" style={{ fontSize:'0.68rem' }}>
                            {d.interest_rate}% interés
                          </span>
                        )}
                        {daysLeft !== null && !paid && (
                          <span className={`badge ${daysLeft < 0 ? 'badge-red' : daysLeft < 15 ? 'badge-yellow' : 'badge-blue'}`} style={{ fontSize:'0.68rem' }}>
                            {daysLeft < 0 ? `Vencida hace ${Math.abs(daysLeft)}d` : `Vence en ${daysLeft}d`}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                      <button className="btn btn-ghost btn-icon btn-sm" onClick={() => openEdit(d)}>✏️</button>
                      <button className="btn btn-danger btn-icon btn-sm" onClick={() => handleDelete(d.id)}>🗑</button>
                    </div>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                    <span style={{ fontSize:'0.82rem', color:'var(--text-secondary)' }}>
                      Pagado: {formatCurrency(d.total_amount - d.remaining_amount, currency)}
                    </span>
                    <span style={{ fontWeight:700, color: d.type==='owe' ? 'var(--red)' : 'var(--green)' }}>
                      {formatCurrency(d.remaining_amount, currency)} restante
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width:`${pct}%`, background: d.type==='owe' ? 'var(--accent)' : 'var(--green)' }} />
                  </div>
                  {!paid && d.type === 'owe' && (
                    <button className="btn btn-secondary btn-sm btn-full" style={{ marginTop:12 }}
                      onClick={() => { setPayModal(d.id); setPayAmt(''); }}>
                      Registrar pago
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {!loading && debts.length === 0 && (
        <div style={styles.empty}>
          <span style={{ fontSize:52 }}>🎊</span>
          <p style={{ fontWeight:600, fontSize:'1.05rem' }}>Sin deudas registradas</p>
          <p style={{ fontSize:'0.85rem' }}>Registra deudas propias o lo que te deben</p>
          <button className="btn btn-primary" onClick={openAdd}>+ Registrar deuda</button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">{editId?'Editar':'Nueva'} deuda</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave} style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div style={styles.typeToggle}>
                {[['owe','💳 Yo debo'],['owed','🤝 Me deben']].map(([v,l]) => (
                  <button key={v} type="button" onClick={() => setForm({...form, type:v})}
                    style={{ ...styles.typeBtn, ...(form.type===v ? styles.typeBtnActive : {}) }}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="form-group">
                <label className="form-label">Nombre / descripción</label>
                <input type="text" className="form-input" placeholder="Ej: Tarjeta de crédito Bancolombia"
                  value={form.name} onChange={e => setForm({...form, name:e.target.value})} required />
              </div>
             <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div className="form-group">
                  <label className="form-label">Monto total</label>
                  <div onClick={() => { setActiveKeypad('total'); setShowKeypad(true); }} style={styles.amountDisplay}>
                    <span style={{ fontSize: form.total_amount ? '1.2rem' : '0.85rem', fontWeight:800, fontFamily:'var(--font-display)', color: form.total_amount ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                      {form.total_amount ? formatCurrency(parseFloat(form.total_amount), currency) : 'Toca aquí'}
                    </span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Saldo pendiente</label>
                  <div onClick={() => { setActiveKeypad('remaining'); setShowKeypad(true); }} style={styles.amountDisplay}>
                    <span style={{ fontSize: form.remaining_amount ? '1.2rem' : '0.85rem', fontWeight:800, fontFamily:'var(--font-display)', color: form.remaining_amount ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                      {form.remaining_amount ? formatCurrency(parseFloat(form.remaining_amount), currency) : 'Toca aquí'}
                    </span>
                  </div>
                </div>
              </div>
              {showKeypad && (activeKeypad === 'total' || activeKeypad === 'remaining') && (
                <NumericKeypad
                  value={activeKeypad === 'total' ? form.total_amount : form.remaining_amount}
                  onChange={(val) => setForm({...form, [activeKeypad === 'total' ? 'total_amount' : 'remaining_amount']: val})}
                  onConfirm={() => setShowKeypad(false)}
                  currency={currency}
                  label={activeKeypad === 'total' ? 'Monto total' : 'Saldo pendiente'}
                />
              )}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div className="form-group">
                  <label className="form-label">Interés % (anual)</label>
                  <input type="number" className="form-input" placeholder="0" min="0" step="0.1"
                    value={form.interest_rate} onChange={e => setForm({...form, interest_rate:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha límite</label>
                  <input type="date" className="form-input" value={form.due_date}
                    onChange={e => setForm({...form, due_date:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Ícono</label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {ICONS.map(ic => (
                    <button key={ic} type="button" onClick={() => setForm({...form, icon:ic})}
                      style={{ width:38, height:38, borderRadius:10, border: form.icon===ic ? '2px solid var(--accent)' : '1px solid var(--border)',
                        background: form.icon===ic ? 'var(--accent-glow)' : 'transparent', cursor:'pointer', fontSize:18 }}>
                      {ic}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button type="button" className="btn btn-secondary" style={{ flex:1 }} onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ flex:2 }} disabled={saving}>
                  {saving ? <span className="spinner" /> : (editId?'Guardar':'Registrar deuda')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pay modal */}
      {payModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setPayModal(null)}>
          <div className="modal" style={{ maxWidth:360 }}>
            <div className="modal-header">
              <h3 className="modal-title">Registrar pago</h3>
              <button className="modal-close" onClick={() => setPayModal(null)}>✕</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div className="form-group">
                <label className="form-label">¿Cuánto pagaste?</label>
                <div onClick={() => { setActiveKeypad('pay'); setShowKeypad(true); }} style={styles.amountDisplay}>
                  <span style={{ fontSize: payAmt ? '1.6rem' : '1rem', fontWeight:800, fontFamily:'var(--font-display)', color: payAmt ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {payAmt ? formatCurrency(parseFloat(payAmt), currency) : 'Toca para ingresar monto'}
                  </span>
                </div>
                {showKeypad && activeKeypad === 'pay' && (
                  <NumericKeypad
                    value={payAmt}
                    onChange={setPayAmt}
                    onConfirm={() => setShowKeypad(false)}
                    currency={currency}
                    label="¿Cuánto pagaste?"
                  />
                )}
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button className="btn btn-secondary" style={{ flex:1 }} onClick={() => setPayModal(null)}>Cancelar</button>
                <button className="btn btn-primary" style={{ flex:2 }} onClick={handlePay}>Registrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { display:'flex', flexDirection:'column', gap:20 },
  summaryGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 },
  empty: { display:'flex', flexDirection:'column', alignItems:'center', gap:14, padding:'60px 20px', color:'var(--text-secondary)', textAlign:'center' },
  typeToggle: { display:'flex', gap:8, background:'var(--bg-input)', borderRadius:'var(--radius-sm)', padding:4 },
  typeBtn: { flex:1, padding:'10px', border:'none', borderRadius:8, cursor:'pointer', fontFamily:'var(--font-body)', fontSize:'0.88rem', fontWeight:600, background:'transparent', color:'var(--text-muted)', transition:'all 0.2s' },
  typeBtnActive: { background:'var(--bg-card-hover)', color:'var(--text-primary)', boxShadow:'inset 0 0 0 1px var(--border)' },
  amountDisplay: {
  background: 'var(--bg-input)',
  border: '1.5px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '16px',
  cursor: 'pointer',
  minHeight: 58,
  display: 'flex',
  alignItems: 'center',
  transition: 'border-color 0.2s',
},
};
